import React, { useState, useEffect } from "react";
import { Card, CardContent } from "../ui/card";
import { AlertCircle, Loader, ArrowRight } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import ExtendedWarrantyScreenWrapper, {
  ExtendedWarrantyHeader,
  ExtendedWarrantyCard,
  ExtendedWarrantyButton,
} from "./ExtendedWarrantyScreenWrapper";
import giftTime from "../../assets/images/gift-time.svg";
import BuyerInfoModal from "../BuyerInfoModal";
import analyticsService from "../../lib/AnalyticsService";
import { useInteractionTracking } from "../../hooks/useAnalytics";

const ExtendedWarrantyOfferScreen = ({
  warrantyProvider,
  onAcceptOffer,
  onReject,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [error, setError] = useState(null);
  const { getIdToken } = useAuth();
  const { trackButtonClick } = useInteractionTracking();

  useEffect(() => {
    analyticsService.trackPageView("warranty_offer", {
      provider: warrantyProvider?.name,
    });
  }, []);

  useEffect(() => {
    // Scroll the page to the top when this screen mounts or provider changes
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [warrantyProvider]);

  if (!warrantyProvider) return null;

  const handleOfferResponse = async (accepted) => {
    setIsProcessing(true);
    setError(null);

    try {
      trackButtonClick(
        accepted ? "accept-warranty-offer" : "reject-warranty-offer",
        {
          step_name: "warranty_offer",
          provider: warrantyProvider.name,
          offer_amount: warrantyProvider.offer?.amount,
        }
      );

      if (accepted) {
        // Process the warranty offer acceptance
        onAcceptOffer(warrantyProvider);
      } else {
        onReject();
      }
    } catch (err) {
      console.error("Error processing warranty offer:", err);
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <ExtendedWarrantyScreenWrapper>
      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm mb-4">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p>{error}</p>
        </div>
      )}

      <ExtendedWarrantyHeader title="YOU'VE GOT AN OFFER FROM" subtitle="" />

      <ExtendedWarrantyCard>
        <div className="space-y-6">
          {/* Provider Logo */}
          <div className="flex justify-center">
            <img
              className="h-16 w-auto object-contain"
              src="https://ownlicdn.blob.core.windows.net/assets/chaiz.png"
              alt="Chaiz logo"
            />
          </div>

          {/* About Chaiz Content */}
          <div className="space-y-4 text-left">
            <p className="text-base font-semibold text-darkPurple">
              Chaiz helps you shield yourself from unexpected vehicle costs.
            </p>

            <p className="text-sm text-gray-700">
              Without a factory warranty, repairs can be costly. An extended
              vehicle warranty protects you from sudden expenses and provides
              peace of mind.
            </p>

            <div className="space-y-2">
              <p className="text-base font-semibold text-darkPurple">
                The nation's only comparison site for extended vehicle
                warranties.
              </p>
              <p className="text-sm text-gray-700">
                Chaiz shows you several plans from multiple providers within 30
                seconds. Compare and buy breakdown protection from top providers
                today.
              </p>
            </div>

            <div className="space-y-2">
              <p className="text-base font-semibold text-darkPurple">
                Protect your car, save big, and get peace of mind with Chaiz.
              </p>
              <ul className="text-sm text-gray-700 space-y-2 list-disc pl-5">
                <li>
                  Save 60% on coverage with the flexibility to cancel anytime
                  and a 30-day money-back guarantee
                </li>
                <li>
                  Avoid costly repairs—like a $5,000 transmission bill—with a
                  Chaiz plan that comes with only a $100 deductible
                </li>
              </ul>
            </div>
          </div>
        </div>
      </ExtendedWarrantyCard>

      {/* Offer Amount (if applicable) */}
      {warrantyProvider.offer?.amount && (
        <div className="w-fit mx-auto flex items-center justify-center p-5 bg-lightGrey rounded-[20px] my-4">
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-1">Coverage Value</p>
            <h3 className="text-xl font-bold">
              ${warrantyProvider.offer.amount.toLocaleString()}
            </h3>
          </div>
        </div>
      )}

      {/* Coverage Benefits */}
      <Card className="shadow-none bg-[#CEC5FF66] border-none !mt-1 rounded-[20px] !mb-4">
        <CardContent className="p-5">
          <div className="flex items-center gap-3">
            <img src={giftTime} alt="giftTime" />
            <p className="text-base text-darkPurple">
              Your reward is pending until your profile is approved
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="space-y-3 mt-auto">
        <ExtendedWarrantyButton
          onClick={() => handleOfferResponse(true)}
          disabled={isProcessing}
          className="w-full"
        >
          {isProcessing ? (
            <>
              <Loader className="w-4 h-4 animate-spin" />
              <span>Processing...</span>
            </>
          ) : (
            <>
              Continue
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </ExtendedWarrantyButton>

        {/* <button
          onClick={() => handleOfferResponse(false)}
          className="w-full text-center text-gray-600 hover:text-gray-900 text-sm py-2"
        >
          No thanks, I'll pass
        </button> */}
      </div>
    </ExtendedWarrantyScreenWrapper>
  );
};

export default ExtendedWarrantyOfferScreen;
